/**
 * Community Live - YouTube Live sharing in Comunidad.
 * Users paste a YouTube live URL and the platform embeds the player.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Heart, VideoCamera, StopCircle, Trash, Broadcast, Eye, YoutubeLogo, Record } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import NativeRecorderDialog from "@/components/NativeRecorderDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function LivePlayer({ live }) {
  if (live.kind === "native") {
    return (
      <div className="relative aspect-video bg-black rounded-xl overflow-hidden border border-white/10">
        <video
          className="absolute inset-0 w-full h-full object-contain bg-black"
          src={`${BACKEND_URL}/api/community/live/media/${live.id}`}
          controls
          playsInline
          preload="metadata"
        />
      </div>
    );
  }
  const videoId = live.status === "live" ? live.video_id : (live.archived_video_id || live.video_id);
  return (
    <div className="relative aspect-video bg-black rounded-xl overflow-hidden border border-white/10">
      <iframe
        className="absolute inset-0 w-full h-full"
        src={`https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0&modestbranding=1`}
        title={live.title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

function GoLiveDialog({ open, onClose, onCreated }) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sport, setSport] = useState("padel");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setUrl(""); setTitle(""); setDescription(""); setSport("padel"); }
  }, [open]);

  const submit = async () => {
    if (!url || !title) { toast.error("Pega la URL del Live y agrega un título"); return; }
    setSaving(true);
    try {
      const r = await api.post("/community/live", { youtube_url: url, title, description, sport });
      toast.success("¡Estás en vivo! Tu transmisión aparece en Comunidad.");
      onCreated?.(r.data.live || null);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "URL de YouTube inválida");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#1A1A1A] border-white/10 text-white" data-testid="live-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase flex items-center gap-2">
            <Broadcast size={20} className="text-rose-500" weight="fill" /> Transmite en Vivo
          </DialogTitle>
          <DialogDescription className="text-white/60 text-xs">
            Pega el enlace de tu transmisión en YouTube Live. Al cortar el video, YouTube deja el VOD disponible para que la comunidad lo revise.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase text-white/70">YouTube Live URL</Label>
            <Input
              placeholder="https://youtube.com/live/XXXX o https://youtu.be/XXXX"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="bg-black/40 border-white/20"
              data-testid="live-url-input"
            />
            <p className="text-[10px] text-white/40 mt-1">También acepta watch?v=, /embed/, /shorts/ y /live/.</p>
          </div>
          <div>
            <Label className="text-xs uppercase text-white/70">Título</Label>
            <Input
              placeholder="Final de la Reta del Sábado"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-black/40 border-white/20"
              data-testid="live-title-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase text-white/70">Descripción (opcional)</Label>
            <Textarea
              placeholder="Partido semifinal entre..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-black/40 border-white/20"
              data-testid="live-desc-input"
              rows={3}
            />
          </div>
          <div>
            <Label className="text-xs uppercase text-white/70">Deporte</Label>
            <div className="grid grid-cols-5 gap-1 mt-1">
              {["padel", "tenis", "corredor", "balonpie", "general"].map((s) => (
                <button
                  key={s}
                  onClick={() => setSport(s)}
                  className={`px-2 py-1.5 rounded text-[11px] font-bold uppercase tracking-wide ${sport === s ? "bg-[#CCFF00] text-black" : "bg-white/5 text-white/70 border border-white/10"}`}
                  data-testid={`live-sport-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          <Button
            onClick={submit}
            disabled={saving || !url || !title}
            className="w-full bg-rose-500 hover:bg-rose-600 text-white font-bold uppercase tracking-wide"
            data-testid="live-submit-btn"
          >
            <Broadcast size={16} className="mr-1" weight="fill" /> {saving ? "Publicando..." : "Ir Live"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LiveCard({ live, me, onEnd, onDelete, onLike }) {
  const owner = live.user_id === me?.id || me?.role === "admin";
  const isLive = live.status === "live";
  const isNative = live.kind === "native";
  const archivedId = live.archived_video_id;
  const liked = (live.likes || []).includes(me?.id);
  const likeCount = (live.likes || []).length;

  return (
    <div className="card-tactical overflow-hidden" data-testid={`live-card-${live.id}`}>
      <div className="relative">
        <LivePlayer live={live} />
        {isLive && !isNative && (
          <div className="absolute top-3 left-3 px-2 py-1 rounded-md bg-rose-500 text-white text-[10px] font-black uppercase tracking-widest flex items-center gap-1 animate-pulse">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" /> Live
          </div>
        )}
        {!isLive && !isNative && (
          <div className="absolute top-3 left-3 px-2 py-1 rounded-md bg-white/15 backdrop-blur text-white text-[10px] font-black uppercase tracking-widest border border-white/20">
            Grabación
          </div>
        )}
        {isNative && (
          <div className="absolute top-3 left-3 px-2 py-1 rounded-md bg-gradient-to-r from-pink-500 to-rose-500 text-white text-[10px] font-black uppercase tracking-widest border border-white/20 flex items-center gap-1">
            <VideoCamera size={10} weight="fill" /> Clip
          </div>
        )}
      </div>
      <div className="p-4 space-y-2">
        <div className="flex items-start gap-3">
          {live.user_photo ? (
            <img src={live.user_photo} alt={live.user_name} className="w-10 h-10 rounded-full object-cover border border-white/15" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-500 to-purple-700 flex items-center justify-center font-black text-white">
              {(live.user_name || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-['Barlow_Condensed'] text-xl font-black uppercase text-white leading-tight truncate">{live.title}</h3>
            <p className="text-xs text-white/60">por {live.user_name} · <span className="capitalize">{live.sport || "general"}</span></p>
          </div>
        </div>
        {live.description && <p className="text-xs text-white/70 line-clamp-2">{live.description}</p>}
        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-2">
            <button
              onClick={() => onLike?.(live.id)}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold uppercase ${liked ? "bg-rose-500 text-white" : "bg-white/5 text-white/80 hover:bg-white/10"}`}
              data-testid={`live-like-${live.id}`}
            >
              <Heart size={12} weight={liked ? "fill" : "regular"} /> {likeCount}
            </button>
            <span className="inline-flex items-center gap-1 text-xs text-white/50">
              <Eye size={12} /> {live.viewers_count || 0}
            </span>
          </div>
          {owner && (
            <div className="flex items-center gap-1">
              {isLive && !isNative && (
                <Button size="sm" variant="outline" onClick={() => onEnd?.(live)} className="border-white/15 text-white/80 h-8 text-[11px] uppercase" data-testid={`live-end-${live.id}`}>
                  <StopCircle size={12} className="mr-1" /> Cortar
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => onDelete?.(live)} className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 w-8 p-0" data-testid={`live-delete-${live.id}`}>
                <Trash size={12} />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CommunityLive() {
  const { user } = useAuth();
  const [filter, setFilter] = useState("live"); // live | ended
  const [lives, setLives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [openRecorder, setOpenRecorder] = useState(false);

  const load = async (status = filter) => {
    setLoading(true);
    try {
      const r = await api.get(`/community/live?status=${status}&limit=50`);
      setLives(r.data.lives || []);
    } catch (e) {
      toast.error("No se pudo cargar los lives");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const endLive = async (live) => {
    const archived = window.prompt("¿URL del VOD archivado en YouTube (o deja vacío para usar el mismo)?", "");
    try {
      await api.post(`/community/live/${live.id}/end`, { archived_video_id: archived || null });
      toast.success("Live finalizado. Quedó archivado en Comunidad.");
      load(filter);
    } catch (e) { toast.error("Error al finalizar"); }
  };
  const deleteLive = async (live) => {
    if (!window.confirm("¿Eliminar este live?")) return;
    try {
      await api.delete(`/community/live/${live.id}`);
      toast.success("Eliminado");
      load(filter);
    } catch (e) { toast.error("Error al eliminar"); }
  };
  const likeLive = async (liveId) => {
    try {
      const r = await api.post(`/community/live/${liveId}/like`);
      setLives((ls) => ls.map((x) => x.id === liveId ? { ...x, likes: r.data.liked ? [...(x.likes || []), user.id] : (x.likes || []).filter(i => i !== user.id) } : x));
    } catch (e) { toast.error("Error al dar like"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-rose-400 mb-1 flex items-center gap-1">
            <YoutubeLogo size={12} weight="fill" /> Lives de la comunidad
          </p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">
            Comunidad <span className="text-rose-500">Live</span>
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={() => setOpenRecorder(true)} variant="outline" className="border-rose-500/50 text-rose-200 hover:bg-rose-500/10 font-bold uppercase tracking-wide" data-testid="live-record-btn">
            <Record size={16} weight="fill" className="mr-1 text-rose-500" /> Grabar clip
          </Button>
          <Button onClick={() => setOpenCreate(true)} className="bg-rose-500 hover:bg-rose-600 text-white font-bold uppercase tracking-wide" data-testid="live-go-live-btn">
            <Broadcast size={16} weight="fill" className="mr-1" /> Ir en Vivo
          </Button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-white/10">
        <button
          onClick={() => setFilter("live")}
          className={`px-4 py-2 text-sm font-bold uppercase tracking-wide ${filter === "live" ? "text-rose-400 border-b-2 border-rose-500" : "text-white/50 hover:text-white"}`}
          data-testid="live-tab-active"
        >
          En vivo
        </button>
        <button
          onClick={() => setFilter("ended")}
          className={`px-4 py-2 text-sm font-bold uppercase tracking-wide ${filter === "ended" ? "text-rose-400 border-b-2 border-rose-500" : "text-white/50 hover:text-white"}`}
          data-testid="live-tab-ended"
        >
          Grabaciones
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-10 h-10 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : lives.length === 0 ? (
        <div className="text-center p-10 border border-dashed border-white/10 rounded-xl">
          <Broadcast size={56} weight="duotone" className="mx-auto text-rose-500/40 mb-2" />
          <p className="text-white/60">{filter === "live" ? "Nadie está transmitiendo. ¡Sé el primero!" : "Aún no hay grabaciones archivadas."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {lives.map((live) => (
            <LiveCard key={live.id} live={live} me={user} onEnd={endLive} onDelete={deleteLive} onLike={likeLive} />
          ))}
        </div>
      )}

      <GoLiveDialog open={openCreate} onClose={() => setOpenCreate(false)} onCreated={() => load(filter)} />
      <NativeRecorderDialog
        open={openRecorder}
        onClose={() => setOpenRecorder(false)}
        onPublished={() => { setFilter("ended"); load("ended"); }}
      />
    </div>
  );
}
